<template>
  <!-- Proyectos en situación crítica (**): una columna por criterio; muestra el dato que lo activa
       (rojo si dispara el criterio, gris en caso contrario). Solo aparecen proyectos en rojo. -->
  <summary-card :title="$t('prm_portfolio_critical_title')" :subtitle="subtitle" :mode="mode">
    <div class="ct-wrap">
      <table class="ct">
        <thead>
          <tr>
            <th class="left">{{ $t('prm_portfolio_col_project') }}<source-chip v-if="mode" source="admin"></source-chip></th>
            <th class="left">{{ $t('prm_portfolio_col_group') }}<source-chip v-if="mode" source="admin"></source-chip></th>
            <th class="left">{{ $t('prm_portfolio_col_client') }}<source-chip v-if="mode" source="resumen"></source-chip></th>
            <th class="left">{{ $t('prm_portfolio_col_pm') }}<source-chip v-if="mode" source="prm"></source-chip></th>
            <th v-for="c in criteria" :key="c.key" class="center crit" v-tip="$t(c.srcNote)">
              {{ $t(c.label) }}<source-chip v-if="mode" :source="c.source"></source-chip>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.id" class="crow" role="button" tabindex="0"
            :style="{ boxShadow: 'inset 3px 0 0 0 ' + SEM.red }"
            @click="$emit('open', r)" @keydown.enter="$emit('open', r)" @keydown.space.prevent="$emit('open', r)">
            <td class="left proj">
              <span class="p-code" :style="{ color: r.groupColor }">{{ r.code || '-' }}</span><span class="p-name" v-tip="r.name">{{ r.name }}</span>
            </td>
            <td class="left"><group-tag :group="r.group" :color="r.groupColor"></group-tag></td>
            <td class="left ptrunc" v-tip="r.client">{{ r.client || '-' }}</td>
            <td class="left ptrunc">{{ r.pm || '-' }}</td>
            <td v-for="c in criteria" :key="c.key" class="center">
              <span :class="'cst-' + cell(r, c.key).state" v-tip="cellTip(r, c)">{{ cell(r, c.key).text }}</span>
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td :colspan="4 + criteria.length" class="empty">{{ $t('prm_portfolio_critical_empty') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Leyenda desplegable: criterio y fuente de datos de cada columna. -->
    <legend-footer>
      <div class="ct-leg">
        <div v-for="c in criteria" :key="c.key" class="ct-leg-row">
          <span class="ct-leg-head"><span class="ct-dot"></span>{{ $t(c.label) }}</span>
          <span class="ct-leg-txt">{{ $t('prm_portfolio_leg_crit_' + c.key) }}
            <em>{{ $t('prm_portfolio_source_label') }}: {{ $t(c.srcNote) }}.</em></span>
        </div>
      </div>
    </legend-footer>
  </summary-card>
</template>

<script>
  import SummaryCard from '../summary/summary-card.vue'
  import SourceChip from '../summary/source-chip.vue'
  import GroupTag from './group-tag.vue'
  import LegendFooter from './legend-footer.vue'
  import { tip } from '../shared/tip'
  import { fmtPct, fmtEur } from '../shared/format'
  import { SEM } from '../dashboard/dashboard-model'
  import { semState, semProgress, semMargin, semBilling, progressDev, marginDevPp } from './portfolio-model'

  // Cada criterio con su fuente real (chip corto + nota para el tooltip): Avance = desviación calculada
  // (Mediciones vs Planificación); Margen = PRM; Facturación = plan de cobro S03; Contractual = revisión
  // contractual (hitos penalizando + riesgos críticos); Safety = incidencias OSH; Calidad = incidencias críticas.
  const CRITERIA = [
    { key: 'progress', label: 'prm_portfolio_crit_progress', source: 'calc', srcNote: 'prm_portfolio_crit_progress_src' },
    { key: 'margin', label: 'prm_portfolio_crit_margin', source: 'prm', srcNote: 'prm_portfolio_crit_margin_src' },
    { key: 'billing', label: 'prm_portfolio_crit_billing', source: 'fact', srcNote: 'prm_portfolio_crit_billing_src' },
    { key: 'contractual', label: 'prm_portfolio_crit_contractual', source: 'resumen', srcNote: 'prm_portfolio_crit_contractual_src' },
    { key: 'safety', label: 'prm_portfolio_crit_safety', source: 'incid', srcNote: 'prm_portfolio_crit_safety_src' },
    { key: 'quality', label: 'prm_portfolio_crit_quality', source: 'incid', srcNote: 'prm_portfolio_crit_quality_src' },
  ]

  export default {
    name: 'PrmPortfolioCriticalProjects',

    components: { SummaryCard, SourceChip, GroupTag, LegendFooter },

    directives: { tip },

    props: {
      allRows: { type: Array, default: () => [] }, // todas las filas normalizadas (se filtran a las críticas)
      mode: { type: Boolean, default: false },
    },

    data: () => ({ criteria: CRITERIA, SEM }),

    computed: {
      // Solo los proyectos en situación crítica (estado general rojo).
      rows () { return this.allRows.filter((r) => semState(r) === 'red') },
      subtitle () {
        return `${this.$t('prm_portfolio_critical_subtitle')} · ${this.rows.length} ${this.$t('prm_portfolio_projects_word')}`
      },
    },

    methods: {
      // Valor SIEMPRE visible por criterio + su estado (active = dispara, idle = no dispara, nodata = sin dato).
      cell (r, key) {
        if (key === 'progress') {
          const d = progressDev(r)
          if (d == null) return { text: '-', state: 'nodata' }
          return { text: `${d > 0 ? '+' : ''}${d.toFixed(0)} pp`, state: semProgress(r) === 'red' ? 'active' : 'idle' }
        }
        if (key === 'margin') {
          const d = marginDevPp(r)
          if (d == null) return { text: '-', state: 'nodata' }
          return { text: `${d > 0 ? '+' : ''}${d.toFixed(1)} pp`, state: semMargin(r) === 'red' ? 'active' : 'idle' }
        }
        if (key === 'billing') {
          const b = semBilling(r)
          if (b == null) return { text: '-', state: 'nodata' }
          return { text: `${fmtEur(r.billing.overdue)} ${this.$t('prm_portfolio_crit_overdue')}`, state: b === 'red' ? 'active' : 'idle' }
        }
        if (key === 'contractual') {
          return { text: `${r.contractualRed} ${this.$t('prm_portfolio_crit_elements')}`, state: r.contractualRed > 0 ? 'active' : 'idle' }
        }
        if (key === 'safety') {
          return { text: `${r.oshOpen} OSH`, state: r.oshOpen >= 2 ? 'active' : 'idle' }
        }
        if (key === 'quality') {
          return { text: `${r.criticalIncidents} ${this.$t('prm_portfolio_crit_critical')}`, state: r.criticalIncidents > 0 ? 'active' : 'idle' }
        }
        return { text: '-', state: 'nodata' }
      },
      // Detalle del cálculo por criterio (para el tooltip).
      cellDetail (r, key) {
        if (key === 'progress' && progressDev(r) != null) {
          return `${this.$t('prm_portfolio_crit_progress')}: ${this.n(r.avanceReal)} % / ${this.n(r.avancePlan)} %`
        }
        if (key === 'margin' && marginDevPp(r) != null) {
          return `${this.$t('prm_portfolio_crit_margin')}: ${fmtPct(r.actualMargin)} / ${fmtPct(r.commercialMargin)}`
        }
        if (key === 'billing' && semBilling(r) != null) {
          return `${this.$t('prm_portfolio_crit_billing')}: ${fmtEur(r.billing.overdue)} / ${fmtEur(r.billing.invoiced)}`
        }
        if (key === 'contractual') return `${r.contractualRed} ${this.$t('prm_portfolio_crit_elements')}`
        if (key === 'safety') return `${r.oshOpen} OSH`
        if (key === 'quality') return `${r.criticalIncidents} ${this.$t('prm_portfolio_crit_critical')}`
        return ''
      },
      // Tooltip: detalle del cálculo · estado del criterio · fuente de datos.
      cellTip (r, c) {
        const state = this.cell(r, c.key).state
        const status = state === 'active' ? this.$t('prm_portfolio_crit_activated')
          : state === 'nodata' ? this.$t('prm_portfolio_crit_no_source')
          : this.$t('prm_portfolio_crit_idle')
        const detail = this.cellDetail(r, c.key)
        return `${detail ? detail + ' · ' : ''}${status} · ${this.$t('prm_portfolio_source_label')}: ${this.$t(c.srcNote)}`
      },
      n (v) { return v == null ? '-' : Number(v).toFixed(1) },
    },
  }
</script>

<style lang="scss" scoped>
  .ct-wrap { overflow-x: auto; }
  .ct { width: 100%; min-width: 1160px; border-collapse: collapse; }
  .ct thead th {
    background: #3d6b9a; color: #fff; font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .03em; padding: 5px 8px; white-space: nowrap;
  }
  .ct thead th.left { text-align: left; }
  .ct thead th.center { text-align: center; }
  .ct tbody td { border-bottom: 1px solid #eef0f3; padding: 5px 8px; font-size: 11px; color: #1e3046; white-space: nowrap; }
  .ct tbody td.left { text-align: left; }
  .ct tbody td.center { text-align: center; font-variant-numeric: tabular-nums; }
  .ct tbody tr:nth-child(even) td { background: #f9fafb; }
  .crow { cursor: pointer; }
  .crow:hover td { background: #fdf3f0; }
  .crow:focus { outline: none; }

  .proj { max-width: 220px; }
  .p-code { font-family: ui-monospace, monospace; font-size: 10px; font-weight: 700; margin-right: 6px; }
  .p-name { font-weight: 600; }
  .ptrunc { max-width: 150px; overflow: hidden; text-overflow: ellipsis; }
  // Criterio: rojo si dispara, gris tenue si no, muy tenue si no hay fuente.
  .cst-active { color: #c0392b; font-weight: 700; }
  .cst-idle { color: #8a94a3; }
  .cst-nodata { color: #c8cfd8; }
  .empty { text-align: center; color: #9aa4b2; padding: 18px; font-size: 11px; }

  // Leyenda desplegable: criterio + fuente por columna.
  .ct-leg { display: grid; grid-template-columns: 1fr; gap: 5px; }
  .ct-leg-row { display: grid; grid-template-columns: 150px 1fr; gap: 10px; align-items: baseline; }
  .ct-leg-head { display: inline-flex; align-items: center; gap: 6px; font-size: 10px; font-weight: 700; color: #1e3046; white-space: nowrap; }
  .ct-dot { width: 8px; height: 8px; border-radius: 50%; background: #e53e3e; flex-shrink: 0; }
  .ct-leg-txt { font-size: 10px; color: #6b7787; line-height: 1.35; }
  .ct-leg-txt em { color: #9aa4b2; font-style: italic; }
</style>
